<template>
  <section class="discovery" :class="{ 'discovery-compact': compact }">
    <div v-if="compact" class="compact-heading">
      <h1>{{ compactTitle || copy.dashboard }}</h1>
    </div>
    <button
      v-if="compact && showAddCourse"
      type="button"
      class="compact-add-course"
      :aria-expanded="searchExpanded"
      aria-controls="discovery-search-panel"
      @click="toggleSearch"
    >
      {{
        searchExpanded
          ? locale === "no"
            ? "Lukk søk"
            : "Close search"
          : locale === "no"
          ? "+ Legg til fag"
          : "+ Add course"
      }}
    </button>
    <div
      v-show="!compact || (showAddCourse && searchExpanded)"
      id="discovery-search-panel"
      class="discovery-grid"
    >
      <div class="discovery-main">
        <template v-if="!compact">
          <div class="eyebrow"><span></span>{{ copy.eyebrow }}</div>
          <h1>
            {{ copy.title }}<br /><span>{{ copy.titleAccent }}</span>
          </h1>
          <p class="discovery-description">{{ copy.description }}</p>
          <router-link class="discovery-primary-link" to="/explore"
            >{{
              locale === "no"
                ? "Finn emner på ditt studieprogram"
                : "Find courses for your programme"
            }}
            <span aria-hidden="true">→</span></router-link
          >
        </template>
        <div class="discovery-search">
          <div class="search-caption">
            {{ compact ? copy.addCourse : copy.searchLabel }}
          </div>
          <slot />
        </div>
        <div v-if="!compact && suggestions.length" class="suggestions">
          <span>{{ copy.try }}</span>
          <button
            v-for="course in suggestions"
            :key="course.course_code"
            type="button"
            @click="$emit('select', course)"
          >
            {{ course.course_code }} <span aria-hidden="true">↗</span>
          </button>
        </div>
        <p v-if="!compact" class="search-footnote">
          <span aria-hidden="true">↳</span> {{ copy.footnote }}
        </p>
      </div>

      <div v-if="!compact" class="preview-stage">
        <div class="preview-orbit" aria-hidden="true"></div>
        <div class="grade-preview">
          <div class="preview-topline">
            <span class="preview-kicker">{{ copy.preview }}</span
            ><span class="demo-label">{{ copy.demo }}</span>
          </div>
          <div class="preview-course">
            <div>
              <h2>{{ copy.exampleCourse }}</h2>
              <p>{{ copy.exampleSubtitle }}</p>
            </div>
            <span class="course-icon" aria-hidden="true">↗</span>
          </div>
          <div class="preview-tabs" :aria-label="copy.chartType">
            <button
              type="button"
              :aria-pressed="mode === 'distribution'"
              @click="mode = 'distribution'"
            >
              {{ copy.distribution }}
            </button>
            <button
              type="button"
              :aria-pressed="mode === 'trend'"
              @click="mode = 'trend'"
            >
              {{ copy.trend }}
            </button>
          </div>
          <div
            v-if="mode === 'distribution'"
            class="demo-chart"
            role="img"
            :aria-label="copy.chartDescription"
          >
            <div class="chart-grid" aria-hidden="true">
              <span>40 %</span><span>20 %</span><span>0 %</span>
            </div>
            <div class="chart-bars" aria-hidden="true">
              <div v-for="bar in bars" :key="bar.label" class="chart-column">
                <span class="bar-value">{{ bar.value }} %</span>
                <div
                  class="chart-bar"
                  :class="{ 'bar-highlight': bar.label === 'C' }"
                  :style="{ height: `${bar.value * 4}px` }"
                ></div>
                <span class="bar-label">{{ bar.label }}</span>
              </div>
            </div>
          </div>
          <div
            v-else
            class="trend-chart"
            role="img"
            :aria-label="copy.trendDescription"
          >
            <svg viewBox="0 0 360 170" aria-hidden="true">
              <path
                d="M0 35H360M0 85H360M0 135H360"
                fill="none"
                stroke="#e5e9e4"
                stroke-dasharray="4 5"
              />
              <path
                d="M10 119 L78 90 L146 107 L214 62 L282 72 L350 34"
                fill="none"
                stroke="#26634d"
                stroke-width="4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="350"
                cy="34"
                r="6"
                fill="#d6f278"
                stroke="#26634d"
                stroke-width="3"
              />
            </svg>
            <div class="trend-years">
              <span>2020</span><span>2022</span><span>2024</span>
            </div>
          </div>
          <div class="preview-summary">
            <div>
              <span>{{ copy.average }}</span
              ><strong>C</strong>
            </div>
            <div>
              <span>{{ copy.pass }}</span
              ><strong>94<span>%</span></strong>
            </div>
            <div>
              <span>{{ copy.grading }}</span
              ><strong>A–F</strong>
            </div>
          </div>
          <p class="preview-note">{{ copy.demoNote }}</p>
        </div>
        <div class="preview-sticker">
          <span aria-hidden="true">✳</span>
          <div>
            {{ copy.sticker }}<br /><strong>{{ copy.stickerBold }}</strong>
          </div>
        </div>
      </div>
    </div>

    <template v-if="!compact">
      <div class="data-strip">
        <span class="data-strip-label">{{ copy.source }}</span
        ><span>HK-dir / DBH</span><span class="strip-divider"></span
        ><span>{{ copy.norwegian }}</span
        ><router-link to="/about"
          >{{ copy.aboutData }} <span aria-hidden="true">↗</span></router-link
        >
      </div>
      <section class="explore-section">
        <div class="explore-heading">
          <span class="eyebrow">{{ copy.exploreEyebrow }}</span>
          <h2>{{ copy.exploreTitle }}</h2>
        </div>
        <div class="explore-grid">
          <router-link to="/explore" class="explore-link explore-featured"
            ><div class="explore-link-top">
              <span class="explore-icon" aria-hidden="true">↗</span
              ><span class="explore-number">01 /</span>
            </div>
            <h3>{{ copy.compareTitle }}</h3>
            <p>{{ copy.compareText }}</p>
            <span class="explore-cta"
              >{{ copy.compareAction }} <span aria-hidden="true">→</span></span
            ></router-link
          >
          <div class="explore-explainer">
            <span class="explore-number">02 /</span>
            <h3>{{ copy.historyTitle }}</h3>
            <p>{{ copy.historyText }}</p>
            <div class="mini-trend" aria-hidden="true">
              <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i
              ><span>↗</span>
            </div>
          </div>
          <div class="explore-explainer">
            <span class="explore-number">03 /</span>
            <h3>{{ copy.sideTitle }}</h3>
            <p>{{ copy.sideText }}</p>
            <div class="mini-comparison" aria-hidden="true">
              <span><i></i><i></i><i></i><i></i></span
              ><span><i></i><i></i><i></i><i></i></span>
            </div>
          </div>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
const copy = {
  no: {
    eyebrow: "LITT MER INNSIKT. LITT MINDRE SYNSING.",
    title: "Ditt neste fag.",
    titleAccent: "Et smartere valg.",
    description:
      "Finn ut hva som venter deg. Utforsk karakterer, se utviklingen og sammenlign fag før du velger.",
    searchLabel: "HVILKET FAG ER DU NYSGJERRIG PÅ?",
    addCourse: "LEGG TIL ET FAG Å SAMMENLIGNE",
    try: "Prøv for eksempel",
    footnote: "Søk med emnekode eller navn. Ingen innlogging nødvendig.",
    preview: "BAK KARAKTERENE",
    demo: "Eksempel",
    exampleCourse: "Et fag. Hele bildet.",
    exampleSubtitle: "Slik kan du utforske karakterstatistikken.",
    chartType: "Velg eksempelgraf",
    distribution: "Karakterfordeling",
    trend: "Utvikling over tid",
    chartDescription:
      "Illustrasjon: A 12 %, B 24 %, C 32 %, D 18 %, E 8 %, F 6 %.",
    trendDescription: "Illustrasjon av karakterutvikling fra 2020 til 2024.",
    average: "Snittkarakter",
    pass: "Bestått",
    grading: "Karakterskala",
    demoNote: "Illustrasjonsdata · Søk etter et fag for faktiske tall.",
    sticker: "Mindre magefølelse.",
    stickerBold: "Mer oversikt.",
    source: "TALL MED EN KILDE",
    norwegian: "Norsk høyere utdanning",
    aboutData: "Om datagrunnlaget",
    exploreEyebrow: "FRA TALL TIL FAGVALG",
    exploreTitle: "Hva vil du finne ut?",
    compareTitle: "Finn fag som passer deg.",
    compareText:
      "Finn emner ved studieprogrammet ditt, lag en kortliste og sammenlign karakterer og stryk.",
    compareAction: "Utforsk fagene",
    historyTitle: "Blir faget vanskeligere?",
    historyText:
      "Se hvordan snittkarakter og strykprosent har endret seg fra år til år.",
    sideTitle: "To fag. Ett bedre valg.",
    sideText: "Søk opp flere fag og sammenlign statistikken side om side.",
  },
  en: {
    eyebrow: "MORE INSIGHT. LESS GUESSWORK.",
    title: "Your next course.",
    titleAccent: "A smarter choice.",
    description:
      "Know what’s ahead. Explore grades, spot trends and compare courses before you choose.",
    searchLabel: "WHICH COURSE ARE YOU CURIOUS ABOUT?",
    addCourse: "ADD A COURSE TO COMPARE",
    try: "Try, for example",
    footnote: "Search by course code or name. No sign-in needed.",
    preview: "BEHIND THE GRADES",
    demo: "Example",
    exampleCourse: "One course. The full picture.",
    exampleSubtitle: "A preview of the statistics you can explore.",
    chartType: "Choose example chart",
    distribution: "Grade distribution",
    trend: "Over time",
    chartDescription: "Illustration: A 12%, B 24%, C 32%, D 18%, E 8%, F 6%.",
    trendDescription: "Illustration of grade trends from 2020 to 2024.",
    average: "Average grade",
    pass: "Passed",
    grading: "Grading scale",
    demoNote: "Illustrative data · Search for a course to see actual figures.",
    sticker: "Less guesswork.",
    stickerBold: "More perspective.",
    source: "DATA WITH A SOURCE",
    norwegian: "Norwegian higher education",
    aboutData: "About the data",
    exploreEyebrow: "FROM NUMBERS TO YOUR NEXT COURSE",
    exploreTitle: "What do you want to know?",
    compareTitle: "Find courses that fit you.",
    compareText:
      "Explore courses linked to your programme, build a shortlist and compare grades and failure rates.",
    compareAction: "Explore courses",
    historyTitle: "Is it getting harder?",
    historyText:
      "See how average grades and failure rates have changed from year to year.",
    sideTitle: "Two courses. A clearer choice.",
    sideText:
      "Search for multiple courses and compare their statistics side by side.",
  },
};
export default {
  name: "CourseDiscovery",
  props: {
    locale: { type: String, default: "no" },
    compact: Boolean,
    compactTitle: { type: String, default: "" },
    showAddCourse: { type: Boolean, default: true },
    courses: { type: Array, default: () => [] },
  },
  emits: ["select"],
  data: () => ({
    searchExpanded: false,
    mode: "distribution",
    bars: [
      { label: "A", value: 12 },
      { label: "B", value: 24 },
      { label: "C", value: 32 },
      { label: "D", value: 18 },
      { label: "E", value: 8 },
      { label: "F", value: 6 },
    ],
  }),
  computed: {
    copy() {
      return {
        ...(copy[this.locale] || copy.en),
        workspace: this.locale === "no" ? "DIN OVERSIKT" : "YOUR WORKSPACE",
        dashboard:
          this.locale === "no" ? "Sammenlign fag." : "Compare courses.",
      };
    },
    suggestions() {
      return ["TMA4100", "TDT4100", "EXPH0300"]
        .map((code) =>
          this.courses.find(
            (course) =>
              course.course_code === code ||
              course.course_code.startsWith(`${code}-`)
          )
        )
        .filter(Boolean);
    },
  },
  methods: {
    toggleSearch() {
      this.searchExpanded = !this.searchExpanded;
      if (this.searchExpanded)
        this.$nextTick(() => this.$el.querySelector("input")?.focus());
    },
  },
};
</script>

<style scoped>
.discovery {
  text-align: left;
}
.discovery-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 64px;
  align-items: center;
  padding: 64px 0 76px;
}
.eyebrow {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.6px;
  color: #496153;
}
.eyebrow > span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #43775a;
}
h1 {
  font-size: clamp(45px, 5.1vw, 70px);
  line-height: 1.06;
  letter-spacing: -3.6px;
  font-weight: 600;
  margin: 24px 0;
}
h1 > span {
  color: #397454;
}
.discovery-description {
  max-width: 420px;
  color: #626e65;
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 20px;
}
.discovery-primary-link {
  display: inline-flex;
  align-items: center;
  gap: 22px;
  min-height: 46px;
  padding: 11px 16px;
  border-radius: 8px;
  background: #254c35;
  color: #f7faee;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 25px;
}
.discovery-primary-link:hover {
  background: #326348;
}
.discovery-primary-link:focus-visible {
  outline: 3px solid #547d40;
  outline-offset: 4px;
}
@media (max-width: 420px) {
  .discovery-primary-link {
    font-size: 12px;
    gap: 13px;
    padding-inline: 13px;
  }
}
.search-caption {
  margin-bottom: 10px;
  font-size: 10px;
  letter-spacing: 1.1px;
  font-weight: 700;
}
.suggestions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 13px;
  font-size: 11px;
  color: #667268;
}
.suggestions button {
  border: 1px solid #dce2d8;
  padding: 5px 9px;
  border-radius: 5px;
  color: #354c3d;
  background: #fff;
  transition: background 0.15s;
}
.suggestions button:hover {
  background: #e8efdd;
}
.suggestions button span {
  margin-left: 7px;
}
.search-footnote {
  color: #6a756d;
  font-size: 11px;
  margin-top: 15px;
}
.search-footnote > span {
  margin-right: 5px;
  color: #397454;
}
.preview-stage {
  position: relative;
  padding: 24px 0 20px;
}
.preview-orbit {
  position: absolute;
  width: 380px;
  height: 380px;
  background: #e7eddf;
  border-radius: 50%;
  top: -9px;
  left: 22px;
}
.grade-preview {
  position: relative;
  background: #fff;
  padding: 25px;
  border: 1px solid #e2e7dd;
  border-radius: 14px;
  box-shadow: 0 22px 60px -30px #314b3c44;
  transform: rotate(-2deg);
}
.preview-topline,
.preview-course {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.preview-kicker {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #5c6c60;
}
.demo-label {
  color: #4b6652;
  background: #eff4e8;
  border: 1px solid #e2e9da;
  border-radius: 4px;
  font-size: 9px;
  padding: 3px 7px;
}
.preview-course {
  margin: 23px 0;
}
.preview-course h2 {
  font-size: 20px;
  letter-spacing: -0.7px;
  font-weight: 600;
}
.preview-course p {
  margin-top: 5px;
  font-size: 11px;
  color: #6d786e;
}
.course-icon {
  display: grid;
  place-items: center;
  width: 35px;
  height: 35px;
  background: #f3f5ef;
  border: 1px solid #e6eade;
  border-radius: 8px;
}
.preview-tabs {
  display: flex;
  padding: 4px;
  gap: 4px;
  border-radius: 6px;
  background: #f1f3ee;
}
.preview-tabs button {
  flex: 1;
  font-size: 10px;
  padding: 7px 3px;
  border-radius: 4px;
  color: #687268;
}
.preview-tabs button[aria-pressed="true"] {
  background: #fff;
  color: #1e3d2b;
  box-shadow: 0 1px 4px #102d1910;
  font-weight: 600;
}
.demo-chart {
  position: relative;
  height: 195px;
  margin-top: 18px;
}
.chart-grid {
  position: absolute;
  inset: 0 0 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.chart-grid span {
  border-bottom: 1px dashed #e2e7df;
  font-size: 8px;
  color: #7d877d;
  padding-bottom: 4px;
}
.chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  position: absolute;
  inset: 15px 3px 0 38px;
}
.chart-column {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  min-width: 0;
}
.chart-bar {
  background: #397454;
  border-radius: 4px 4px 0 0;
  width: 100%;
  max-width: 38px;
}
.bar-highlight {
  background: #d4ed87;
}
.bar-value {
  font-size: 9px;
  color: #57685a;
  margin-bottom: 5px;
}
.bar-label {
  font-size: 10px;
  margin-top: 9px;
  height: 15px;
  color: #667268;
}
.preview-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid #e8ece4;
  margin-top: 19px;
  padding-top: 17px;
}
.preview-summary > div {
  padding-left: 15px;
  border-left: 1px solid #e8ece4;
}
.preview-summary > div:first-child {
  border: 0;
  padding-left: 0;
}
.preview-summary > div > span {
  color: #6e786f;
  font-size: 9px;
  display: block;
  margin-bottom: 6px;
}
.preview-summary strong {
  font-size: 27px;
  letter-spacing: -1px;
  font-weight: 500;
}
.preview-summary strong span {
  font-size: 15px;
  margin-left: 2px;
}
.preview-note {
  font-size: 8px;
  color: #6d786e;
  margin-top: 17px;
}
.preview-sticker {
  display: flex;
  align-items: center;
  gap: 11px;
  position: absolute;
  right: -18px;
  bottom: -62px;
  background: #d4ed87;
  padding: 14px 20px;
  border-radius: 8px;
  transform: rotate(4deg);
  font-size: 11px;
  line-height: 1.6;
  box-shadow: 0 7px 15px #243c2110;
}
.preview-sticker > span {
  font-size: 31px;
}
.preview-sticker strong {
  font-weight: 600;
}
.trend-chart {
  height: 195px;
  margin-top: 18px;
  padding-top: 5px;
}
.trend-chart svg {
  width: 100%;
  height: 165px;
}
.trend-years {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #6d786e;
}
.data-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 22px;
  border-top: 1px solid #dce2d7;
  border-bottom: 1px solid #dce2d7;
  padding: 21px 0;
  font-size: 11px;
  color: #5f6d61;
}
.data-strip-label {
  font-size: 9px;
  letter-spacing: 1.3px;
  font-weight: 600;
}
.data-strip > span:nth-child(2) {
  font-weight: 700;
  color: #354c3d;
  font-size: 14px;
  letter-spacing: -0.4px;
}
.strip-divider {
  width: 1px;
  height: 15px;
  background: #d7ded1;
}
.data-strip a {
  margin-left: auto;
  text-decoration: none;
  color: #344e3b;
}
.data-strip a span {
  margin-left: 12px;
}
.explore-section {
  padding: 50px 0 45px;
}
.explore-heading h2 {
  font-size: 29px;
  letter-spacing: -1px;
  font-weight: 500;
  margin: 10px 0 25px;
}
.explore-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}
.explore-featured {
  background: #213f30;
  border-radius: 10px;
  color: #fff;
  padding: 23px;
  text-decoration: none;
  transition: background 0.2s, transform 0.2s;
}
.explore-featured:hover {
  background: #2d523e;
  transform: translateY(-3px);
}
.explore-link-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}
.explore-icon {
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #d4ed87;
  color: #213f30;
  width: 32px;
  height: 32px;
  font-size: 20px;
}
.explore-number {
  font-size: 10px;
  letter-spacing: 1px;
  color: #758272;
}
.explore-featured .explore-number {
  color: #b6cbb4;
}
.explore-grid h3 {
  font-size: 17px;
  letter-spacing: -0.5px;
  font-weight: 500;
  margin-bottom: 9px;
}
.explore-grid p {
  font-size: 12px;
  line-height: 1.8;
  color: #677166;
}
.explore-featured p {
  color: #c4d3c3;
}
.explore-cta {
  display: flex;
  justify-content: space-between;
  margin-top: 22px;
  padding-top: 15px;
  border-top: 1px solid #ffffff29;
  color: #deefc4;
  font-size: 11px;
}
.explore-explainer {
  padding: 20px 18px 10px;
  border-top: 1px solid #d5ddd0;
}
.explore-explainer > .explore-number {
  display: block;
  margin: 4px 0 24px;
}
.mini-trend {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 55px;
  margin-top: 16px;
}
.mini-trend i {
  width: 13px;
  background: #d4deca;
  border-radius: 2px 2px 0 0;
  height: 20px;
}
.mini-trend i:nth-child(2n) {
  height: 30px;
}
.mini-trend i:nth-child(n + 5) {
  height: 38px;
}
.mini-trend i:nth-child(n + 7) {
  height: 47px;
  background: #548061;
}
.mini-trend span {
  margin: 0 0 12px 10px;
  color: #397454;
  font-size: 25px;
}
.mini-comparison {
  display: flex;
  gap: 12px;
  margin-top: 25px;
}
.mini-comparison > span {
  display: flex;
  align-items: flex-end;
  gap: 5px;
  border: 1px solid #dae2d3;
  background: #f0f3ea;
  padding: 10px;
  border-radius: 5px;
  height: 48px;
}
.mini-comparison i {
  height: 15px;
  width: 9px;
  background: #64836a;
}
.mini-comparison i:nth-child(2) {
  height: 25px;
}
.mini-comparison i:nth-child(3) {
  height: 19px;
}
.mini-comparison > span:nth-child(2) i {
  background: #bdce98;
}
.discovery-compact .discovery-grid {
  display: block;
  padding: 16px 0 14px;
}
.discovery-compact .discovery-main {
  max-width: 820px;
  margin: auto;
}
.compact-heading {
  padding-top: 24px;
}
.compact-heading h1 {
  font-size: 32px;
  letter-spacing: -1.1px;
  margin: 13px 0 6px;
}
.discovery-compact .discovery-main {
  max-width: none;
}
button {
  cursor: pointer;
}
button:focus-visible,
a:focus-visible {
  outline: 3px solid #397454;
  outline-offset: 4px;
}
@media (min-width: 1500px) {
  h1 {
    font-size: 72px;
  }
}
@media (max-width: 1100px) {
  .discovery-grid {
    gap: 35px;
    padding-top: 42px;
  }
  h1 {
    letter-spacing: -2.6px;
  }
  .grade-preview {
    padding: 20px;
  }
  .preview-orbit {
    width: 310px;
    height: 310px;
    left: 0;
    top: 40px;
  }
  .preview-course h2 {
    font-size: 18px;
  }
  .preview-sticker {
    right: -8px;
  }
  .chart-bars {
    gap: 10px;
  }
  .explore-grid {
    gap: 15px;
  }
}
@media (max-width: 760px) {
  .discovery-grid {
    grid-template-columns: 1fr;
    gap: 25px;
    padding: 30px 0 74px;
  }
  .discovery-main {
    max-width: 540px;
  }
  h1 {
    font-size: clamp(43px, 8.5vw, 62px);
    margin: 22px 0;
  }
  .discovery-description {
    font-size: 15px;
    margin-bottom: 26px;
  }
  .preview-stage {
    width: min(400px, calc(100% - 22px));
    margin: 0 auto;
  }
  .preview-orbit {
    width: 330px;
    height: 330px;
  }
  .explore-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .explore-explainer {
    padding: 20px 5px 0;
  }
  .explore-explainer > .explore-number {
    margin-bottom: 15px;
  }
  .explore-section {
    padding-top: 32px;
  }
  .data-strip {
    gap: 14px;
    padding: 19px 0;
  }
  .data-strip-label {
    width: 100%;
  }
  .data-strip a {
    width: 100%;
    margin: 0;
  }
  .eyebrow {
    font-size: 9px;
    letter-spacing: 1.2px;
  }
  .preview-note {
    font-size: 9px;
  }
}
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
  }
}

.discovery-compact {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
  padding: 20px 0;
}
.discovery-compact .compact-heading {
  padding: 0;
  min-width: 0;
  flex: 1;
}
.discovery-compact .compact-heading h1 {
  margin: 0;
  font-size: 30px;
  letter-spacing: -1.1px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}
.discovery-compact .discovery-grid {
  padding: 0;
  flex-basis: 100%;
}
.compact-add-course {
  font-size: 12px;
  border: 1px solid #ccd9c1;
  border-radius: 7px;
  padding: 10px 14px;
  background: #fff;
  color: #315b3d;
  min-height: 42px;
}
@media (max-width: 980px) {
  .discovery-compact {
    gap: 16px;
    padding: 18px 0;
  }
  .discovery-compact .compact-heading h1 {
    font-size: 27px;
  }
}
@media (max-width: 420px) {
  .discovery-compact .compact-heading h1 {
    font-size: 24px;
  }
  .compact-add-course {
    font-size: 11px;
    padding-inline: 9px;
  }
  .discovery-compact {
    gap: 10px;
  }
}
</style>
